/**
 * The interfaces this page can show (Phase 5b, step 6; design note
 * docs/plans/2026-10-19-engine-interface-separation.md §3.5): classic
 * (interfaces/classic/, in this repository) and others. The person
 * chooses one with the button next to the day/night one, kept on this
 * device; a link (?ui=holon) tries one without keeping it. A change takes
 * effect in place, on the map open (no reload).
 *
 * Another interface is its own page, shown in a frame in place of classic's
 * main screen once signing in (or the signed-out start) is done: its own
 * document, so its styles, element ids and keys stay apart from classic's,
 * on the same engine. It reads `window.parent.OrbitalHost`: { engine, kit }
 * (docs/reference/engine-api.md, "Interfaces"). Classic's sign-in screens,
 * dialogs and messages serve every interface for now, and classic draws
 * nothing meanwhile (main.js asks `isClassicShown`).
 */
import { engine } from "../engine/index.js";
import { getPref, setPref } from "../engine/storage/index.js";
import { on, Events } from "../engine/core/events.js";
import { showToast } from "./classic/ui/toast.js";
import { t } from "../shared/i18n/index.js";

const PREF = "orbital-interface";

/**
 * Classic, and the others offered here, listed by the server in
 * /interfaces/available.json ([{ id, name }]; each one's page at
 * /interfaces/<id>/index.html). The development server lists holon
 * (gitlab.com/bass-lab/holon) when ORBITAL_HOLON_DIR names a clone
 * (vite.config.js; Alex, 2026-10-19, decision 3); a server's build lists
 * those its .env.prod names (docker/interfaces.conf, 2026-10-21); otherwise
 * the list is empty (public/interfaces/available.json).
 */
const CLASSIC = { id: "classic", nameKey: "app.interface.classic" };
// Names an interface may not take: Orbital's own stored keys start with
// them ("orbital-…", "mobile-…"), and an interface's keys are removed at
// sign-out by their "<id>-" start.
export const RESERVED_IDS = new Set(["classic", "orbital", "mobile", "notes", "tree", "routines", "project", "task", "engine", "shared"]);
const INTERFACE_ID = /^[a-z][a-z0-9-]{0,31}$/;

const nameOf = (entry) => (entry.nameKey ? t(entry.nameKey) : entry.name);

let active = "classic";
let offered = [CLASSIC];
let callbacks = {};
// The main screen is shown (not a sign-in screen): the interface shows there.
let mainShown = false;

/** The interface shown on this page: "classic", "holon"… */
export function activeInterface() {
  return active;
}

/** Whether classic draws its views (not while another interface is shown). */
export function isClassicShown() {
  return active === "classic";
}

/** The other interfaces offered here; none when the list cannot be read (offline). */
async function listedInterfaces() {
  try {
    const answer = await fetch("/interfaces/available.json", { cache: "no-store" });
    const list = answer.ok ? await answer.json() : [];
    return (Array.isArray(list) ? list : [])
      .filter((entry) => INTERFACE_ID.test(entry?.id) && !RESERVED_IDS.has(entry.id))
      .map((entry) => ({ id: entry.id, name: String(entry.name || entry.id), page: `/interfaces/${entry.id}/index.html` }));
  } catch {
    return [];
  }
}

/**
 * At start, before anything is drawn: which interface this page shows, and
 * which ones the button offers.
 * @param {{showClassic?: () => void}} [options] - draw classic after all
 *   (the chosen interface could not start)
 */
export async function chooseAtStart(options = {}) {
  callbacks = options;
  const fromLink = new URLSearchParams(location.search).get("ui");
  const wanted = fromLink || getPref(PREF) || "classic";
  // The start waits for the list only when another interface is wanted;
  // otherwise it comes meanwhile, for the button.
  const listed = listedInterfaces();
  const entry = wanted === CLASSIC.id ? null : (await listed).find((candidate) => candidate.id === wanted) || null;
  if (!entry && wanted !== "classic") {
    // Only a name an interface could have: the address is anyone's to write.
    const name = INTERFACE_ID.test(wanted) ? wanted[0].toUpperCase() + wanted.slice(1) : "?";
    showToast(t("app.interface.unavailable", { name }), { type: "warning", duration: 8000 });
  }
  active = entry?.id || "classic";
  offered = entry ? [CLASSIC, entry] : [CLASSIC];
  document.body.dataset.interface = active;
  void listed.then((others) => {
    offered = [CLASSIC, ...others];
    installHeaderButton();
  });
  on(Events.SCREEN_SHOWN, ({ screenId }) => {
    mainShown = screenId === "main-app";
    if (active === "classic") return;
    if (mainShown) void showInterface();
    else document.getElementById("interface-root")?.classList.add("hidden");
  });
}

/**
 * Show `id` now, on the map open, and keep it on this device (the address's
 * ?ui= dropped). Another interface leaves with its frame and everything it
 * listened to; classic draws again over the engine's open map.
 */
function switchTo(id) {
  if (id === active || !offered.some((entry) => entry.id === id)) return;
  setPref(PREF, id);
  const url = new URL(location.href);
  url.searchParams.delete("ui");
  history.replaceState(history.state, "", url);
  unmountInterface();
  active = id;
  document.body.dataset.interface = id;
  for (const option of document.querySelectorAll(".interface-dropdown .theme-option")) {
    option.classList.toggle("active", option.dataset.interface === id);
  }
  if (!mainShown) return;
  if (id === "classic") {
    document.getElementById("main-app")?.classList.remove("hidden");
    callbacks.showClassic?.();
  } else {
    void showInterface();
  }
}

/**
 * Classic's button that chooses the interface (a menu, as the theme's).
 * Null when only one interface is offered.
 */
function createSwitchButton() {
  if (offered.length < 2) return null;
  const wrap = document.createElement("div");
  wrap.className = "interface-selector";
  const button = document.createElement("button");
  button.type = "button";
  button.className = "btn btn-icon";
  button.title = t("app.interface.choose");
  button.setAttribute("aria-label", t("app.interface.choose"));
  // Two panes side by side: another way to see the same maps.
  button.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">'
    + '<rect x="3" y="4" width="18" height="16" rx="2"></rect><line x1="10" y1="4" x2="10" y2="20"></line></svg>';
  const menu = document.createElement("div");
  menu.className = "theme-dropdown interface-dropdown";
  for (const entry of offered) {
    const option = document.createElement("div");
    option.className = "theme-option";
    option.dataset.interface = entry.id;
    option.classList.toggle("active", entry.id === active);
    option.textContent = nameOf(entry);
    option.addEventListener("click", () => {
      menu.classList.remove("active");
      if (entry.id !== active) switchTo(entry.id);
    });
    menu.append(option);
  }
  button.addEventListener("click", (event) => {
    event.stopPropagation();
    menu.classList.toggle("active");
  });
  document.addEventListener("click", (event) => {
    if (!wrap.contains(event.target)) menu.classList.remove("active");
  });
  wrap.append(button, menu);
  return wrap;
}

function installHeaderButton() {
  const theme = document.querySelector("#main-app .theme-selector");
  const button = createSwitchButton();
  if (theme && button) theme.after(button);
}

/** What Orbital gives an interface besides the engine: for its own button that chooses the interface. */
const kit = Object.freeze({
  /** The interfaces offered: [{ id, name, active }]. */
  interfaces: () => offered.map((entry) => ({ id: entry.id, name: nameOf(entry), active: entry.id === active })),
  /** Show another interface, on the map open (kept on this device). */
  choose: (id) => { if (offered.some((entry) => entry.id === id) && id !== active) switchTo(id); },
  /** The page's language ("en", "fr"…). */
  language: () => document.documentElement.lang || "en",
});

let mounting = null;
// Each frame made and gone: a mount still waiting when its frame was
// dropped (an account change) gives up.
let generation = 0;
// What the interface shown listens to on the engine: dropped when it leaves.
const frameStops = [];

/** The interface shown leaves: its frame, its listeners. */
function unmountInterface() {
  generation += 1;
  for (const stop of frameStops.splice(0)) {
    try { stop(); } catch { /* already stopped */ }
  }
  document.getElementById("interface-root")?.remove();
  window.OrbitalHost = undefined;
  mounting = null;
}

// The start's end (signed in with the first map open, or the signed-out
// start, also after signing out): another interface mounts then, on a map
// already open. Signing in or out waits for the next one.
const nextStart = () => new Promise((resolve) => {
  const stop = on(Events.APP_STARTED, () => { stop(); resolve(); });
});
let started = nextStart();

// The interfaces whose frame was shown on this device (kept), for what they
// stored here to be removed at sign-out even when the list is not at hand.
const USED = "orbital-interfaces-used";
function rememberUsed(id) {
  const used = new Set(String(getPref(USED) || "").split(",").filter(Boolean));
  if (!used.has(id)) setPref(USED, [...used, id].join(","));
}

/**
 * What other interfaces stored on this device (their keys start with
 * "<id>-", docs/reference/engine-api.md "Interfaces") goes at sign-out,
 * except what belongs to one map (keys ending ":<mapId>": ids and numbers,
 * never content), like classic's own per-map state.
 */
function forgetInterfacesData() {
  const ids = new Set([...offered.map((entry) => entry.id), ...String(getPref(USED) || "").split(",")]
    .filter((id) => INTERFACE_ID.test(id) && !RESERVED_IDS.has(id)));
  try {
    const keys = Array.from({ length: localStorage.length }, (_, i) => localStorage.key(i));
    for (const key of keys) {
      if (!key || ![...ids].some((id) => key.startsWith(`${id}-`))) continue;
      if (/:[\w-]+$/.test(key)) continue;
      localStorage.removeItem(key);
    }
  } catch (error) {
    console.warn("[Interfaces] What other interfaces stored here was not removed:", error);
  }
}

// Another account (signing in, signing out): the frame held the previous
// account's maps, so it goes now and comes back fresh once the start ends.
// (Text being typed there was saved first: signing out blurs the frame's
// field, engine/auth/handlers.js logout.)
function accountChanged() {
  started = nextStart();
  if (active !== CLASSIC.id) unmountInterface();
}
on(Events.AUTH_LOGIN, accountChanged);
on(Events.AUTH_LOGOUT, () => {
  accountChanged();
  forgetInterfacesData();
});

/** Show the chosen interface where classic's main screen is (its frame made once). */
async function showInterface() {
  document.getElementById("main-app")?.classList.add("hidden");
  let page = document.getElementById("interface-root");
  if (!page) {
    page = document.createElement("div");
    page.id = "interface-root";
    document.body.append(page);
  }
  page.classList.remove("hidden");
  mounting ??= (async () => {
    const entry = offered.find((candidate) => candidate.id === active);
    const mine = generation;
    await started;
    if (mine !== generation) return;
    rememberUsed(entry.id);
    try {
      // The engine as the frame gets it: what it listens to is recorded, to
      // stop when it leaves (its page is gone then). The interface's ways of
      // reaching the person stay classic's for now (no setHost).
      const { setHost: _classicHost, ...api } = engine;
      const frameEngine = Object.freeze({
        ...api,
        on(name, listener) {
          const stop = engine.on(name, listener);
          frameStops.push(stop);
          return stop;
        },
      });
      window.OrbitalHost = Object.freeze({ engine: frameEngine, kit });
      const frame = document.createElement("iframe");
      frame.id = "interface-frame";
      frame.title = nameOf(entry);
      const loaded = new Promise((resolve) => frame.addEventListener("load", resolve, { once: true }));
      frame.src = entry.page;
      page.append(frame);
      await loaded;
      frame.focus();
    } catch (error) {
      console.error(`[Interfaces] ${entry.id} could not start:`, error);
      showToast(t("app.interface.failed", { name: nameOf(entry), error: error?.message || String(error) }),
        { type: "error", duration: 10000 });
      unmountInterface();
      active = "classic";
      document.body.dataset.interface = active;
      document.getElementById("main-app")?.classList.remove("hidden");
      callbacks.showClassic?.();
    }
  })();
  return mounting;
}
